/**
 * The messaging channels that support opt-outs. The wire values match the
 * backend opt-out condition `type` and the customer opt-out API `channel`
 * field (services core/conditions/optout.go).
 */
export const OPT_OUT_CHANNELS = ['sms', 'whatsapp'] as const;

export type OptOutChannel = (typeof OPT_OUT_CHANNELS)[number];

// A channel-less opt-out has always meant SMS, both on the wire and in the
// backend (NormalizeOptOutChannel). Keep that default here so stored
// conditions and empty input resolve the same way.
export const DEFAULT_OPT_OUT_CHANNEL: OptOutChannel = 'sms';

const CHANNEL_LABELS = {
  sms: 'SMS',
  whatsapp: 'WhatsApp',
} satisfies Record<OptOutChannel, string>;

/**
 * Coerces a wire value (case-insensitive) to a known channel, falling back to
 * the default (SMS) for empty, legacy, or unknown input. This mirrors the
 * backend, which treats a channel-less opt-out as SMS.
 */
export function normalizeOptOutChannel(value: string | null | undefined): OptOutChannel {
  const lower = (value ?? '').toLowerCase();
  return OPT_OUT_CHANNELS.find((channel) => channel === lower) ?? DEFAULT_OPT_OUT_CHANNEL;
}

export function optOutChannelLabel(channel: OptOutChannel | null | undefined): string {
  return CHANNEL_LABELS[normalizeOptOutChannel(channel)];
}

/**
 * The slice of a workspace sender identity the opt-out helpers read. SMS
 * senders are stored as template_type 'sms' (custom SMS, the default for
 * workspaces without a Twilio integration) or 'twilio' (Twilio-synced);
 * WhatsApp senders are WhatsApp identities.
 */
export type OptOutSenderIdentity = {
  template_type: string;
  phone: string;
  name: string;
};

// Services accepts both SMS spellings (ui_api/identities/create.go), so both
// must count as the SMS channel here (FPS-3717). The wire is unaffected: the
// serialized condition carries the channel's bitmap type, never the sender's
// template_type.
const CHANNEL_TEMPLATE_TYPES = {
  sms: ['sms', 'twilio'],
  whatsapp: ['whatsapp'],
} satisfies Record<OptOutChannel, readonly string[]>;

export function getOptOutIdentities<TIdentity extends OptOutSenderIdentity>(
  identities: readonly TIdentity[],
  channel: OptOutChannel,
): TIdentity[] {
  const templateTypes = CHANNEL_TEMPLATE_TYPES[channel];
  return identities.filter((identity) => templateTypes.includes(identity.template_type));
}

/**
 * The channels the workspace can build opt-out conditions for — those with at
 * least one configured sender — in canonical order (SMS before WhatsApp).
 */
export function getAvailableOptOutChannels(identities: readonly OptOutSenderIdentity[]): OptOutChannel[] {
  return OPT_OUT_CHANNELS.filter((channel) => getOptOutIdentities(identities, channel).length > 0);
}

export function hasAnyOptOutChannel(identities: readonly OptOutSenderIdentity[]): boolean {
  return getAvailableOptOutChannels(identities).length > 0;
}

/**
 * The channel a newly created opt-out condition should target: the first
 * channel with a configured sender, or SMS when the workspace has none.
 */
export function defaultOptOutChannel(identities: readonly OptOutSenderIdentity[]): OptOutChannel {
  return getAvailableOptOutChannels(identities)[0] ?? DEFAULT_OPT_OUT_CHANNEL;
}

/** Select options for the available channels, for the row's channel picker. */
export function optOutChannelOptions(
  identities: readonly OptOutSenderIdentity[],
): { label: string; value: OptOutChannel }[] {
  return getAvailableOptOutChannels(identities).map((channel) => ({
    label: CHANNEL_LABELS[channel],
    value: channel,
  }));
}
